import { useCallback, useEffect, useState } from 'react'
import { retrieveRawInitData } from '@tma.js/sdk-react'
import { fetchToday, login, type TodayResponse } from './lib/api'
import { TodayScreen } from './screens/TodayScreen'
import { FoodScreen } from './screens/FoodScreen'
import { WorkoutScreen } from './screens/WorkoutScreen'
import { MeasureScreen } from './screens/MeasureScreen'
import { HealthScreen } from './screens/HealthScreen'
import { LabsScreen } from './screens/LabsScreen'
import { LoadError, Spinner } from './components/ui'
import { IconFood, IconGym, IconHeart, IconRings, IconRuler } from './components/icons'

type Tab = 'today' | 'food' | 'workout' | 'measure' | 'health'

/**
 * Кнопка «Править в приложении» под разбором бланка в чате открывает Mini App
 * с ?report=<id> — значит, при старте надо прыгнуть сразу на сверку.
 */
function initialReportFromUrl(): string | undefined {
  return new URLSearchParams(window.location.search).get('report') ?? undefined
}

const TABS = [
  { id: 'today' as const, Icon: IconRings, label: 'Сводка' },
  { id: 'food' as const, Icon: IconFood, label: 'Еда' },
  { id: 'workout' as const, Icon: IconGym, label: 'Тренировка' },
  { id: 'measure' as const, Icon: IconRuler, label: 'Замеры' },
  { id: 'health' as const, Icon: IconHeart, label: 'Здоровье' },
]

export function App() {
  const [reportId] = useState(initialReportFromUrl)
  const [tab, setTab] = useState<Tab>(reportId ? 'health' : 'today')
  const [labsOpen, setLabsOpen] = useState(Boolean(reportId))
  const [reviewingLab, setReviewingLab] = useState(false)
  const [today, setToday] = useState<TodayResponse | null>(null)
  const [error, setError] = useState<string | null>(null)

  const reloadToday = useCallback(async () => {
    try {
      setToday(await fetchToday())
      setError(null)
    } catch (cause) {
      setError((cause as Error).message)
    }
  }, [])

  useEffect(() => {
    let cancelled = false

    async function start() {
      try {
        const initDataRaw = retrieveRawInitData()
        if (!initDataRaw) throw new Error('Telegram не передал данные авторизации')

        await login(initDataRaw)
        if (!cancelled) await reloadToday()
      } catch (cause) {
        if (!cancelled) setError((cause as Error).message)
      }
    }

    void start()
    return () => {
      cancelled = true
    }
  }, [reloadToday])

  // Экран «Сегодня» перечитывается при возврате на него: отметки и еда могли
  // измениться на других вкладках или из чата с ботом.
  useEffect(() => {
    if (tab === 'today') void reloadToday()
  }, [tab, reloadToday])

  if (error && !today) {
    return <LoadError message={error} onRetry={() => void reloadToday()} />
  }
  if (!today) {
    return <Spinner label="Загружаю…" />
  }

  return (
    <div className="flex min-h-full flex-col">
      <main className="flex-1">
        {tab === 'today' && <TodayScreen today={today} onChanged={reloadToday} />}
        {tab === 'food' && <FoodScreen />}
        {tab === 'workout' && <WorkoutScreen />}
        {tab === 'measure' && <MeasureScreen />}
        {tab === 'health' &&
          (labsOpen ? (
            <LabsScreen
              initialReportId={reportId}
              onBack={() => setLabsOpen(false)}
              onReviewing={setReviewingLab}
            />
          ) : (
            <HealthScreen onOpenLabs={() => setLabsOpen(true)} />
          ))}
      </main>

      {/* Во время сверки бланка вкладки скрыты: случайное касание уводило с
          экрана и стирало правки, которые ещё не сохранены. */}
      <nav
        hidden={reviewingLab}
        className="pb-safe fixed inset-x-0 bottom-0 flex border-t border-ink-separator bg-black/80 px-1 pt-1.5 backdrop-blur-xl"
      >
        {TABS.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setTab(item.id)}
            aria-current={tab === item.id ? 'page' : undefined}
            className={`flex min-h-14 flex-1 flex-col items-center justify-center gap-1 text-[10px] tracking-tight ${
              tab === item.id ? 'text-sys-blue' : 'text-ink-3'
            }`}
          >
            <item.Icon size={26} />
            {item.label}
          </button>
        ))}
      </nav>
    </div>
  )
}
